import { useState } from "react";
import { Check, Calendar, User } from "lucide-react";
import type {
  AnswerValue,
  Question,
  Survey,
} from "@shared-domain/survey/contract";
import { isQuestionVisible, radioV } from "@shared-domain/survey/logic";
import { HEARING_QID, METHOD_QID } from "@shared-domain/survey/presets";
import { T, hexA, todayJst, daysAgoJst, toDateInput, fromDateInput } from "./theme";
import { MethodIcon } from "./methods";
import { answerMapToVisMap, type AnswerMap } from "./answers";

function QLabel({ q }: { q: Question }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{q.label}</span>
      {q.required ? (
        <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: T.accent, borderRadius: 4, padding: "1px 6px" }}>必須</span>
      ) : (
        <span style={{ fontSize: 10, fontWeight: 600, color: T.faint, border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 6px" }}>任意</span>
      )}
      {q.hint && <span style={{ fontSize: 11, color: T.faint }}>{q.hint}</span>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, multiline, iconLeft }: {
  value: string; onChange: (v: string) => void; placeholder?: string; multiline?: boolean; iconLeft?: React.ReactNode;
}) {
  const [focus, setFocus] = useState(false);
  const border = `2px solid ${focus ? T.accent : "#E2E8F0"}`;
  const common = { width: "100%", color: T.ink, background: "#fff", border, borderRadius: 12, outline: "none", boxSizing: "border-box" as const };
  if (multiline) {
    return (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ ...common, padding: "12px 14px", fontSize: 14, resize: "none", fontFamily: "inherit", lineHeight: 1.5 }} />
    );
  }
  return (
    <div style={{ position: "relative" }}>
      {iconLeft && <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>{iconLeft}</span>}
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ ...common, height: 48, padding: iconLeft ? "0 14px 0 40px" : "0 14px", fontSize: 15 }} />
    </div>
  );
}

// 実施方法 (__method): 横4つ・ほぼ正方形・アイコンの下にラベル。単一選択の選択色は accent で統一。
function MethodGrid({ q, value, onChange }: {
  q: Extract<Question, { type: "radio" }>; value: AnswerValue | undefined; onChange: (v: AnswerValue) => void;
}) {
  const cur = radioV(value);
  const curText = value && typeof value === "object" && !Array.isArray(value) ? value.text : "";
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        {q.options.map((o) => {
          const on = cur === o.v;
          return (
            <button key={o.v} type="button" onClick={() => onChange(o.withText ? { v: o.v, text: cur === o.v ? curText ?? "" : "" } : o.v)}
              style={{ aspectRatio: "1 / 1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, background: on ? hexA(T.accent, 0.1) : "#fff", border: on ? `2px solid ${T.accent}` : "1px solid #E2E8F0", borderRadius: 12, cursor: "pointer", padding: 4 }}>
              <MethodIcon method={o.v} size={22} color={on ? T.accent : "#64748B"} />
              <span style={{ fontSize: 12.5, fontWeight: on ? 700 : 600, color: on ? T.accent : "#334155" }}>{o.label}</span>
            </button>
          );
        })}
      </div>
      {q.options.some((o) => o.withText && cur === o.v) && (
        <div style={{ marginTop: 8 }}>
          <TextInput value={curText ?? ""} onChange={(t) => onChange({ v: cur ?? "", text: t })} placeholder="内容を入力" />
        </div>
      )}
    </div>
  );
}

function QuestionField({ q, value, onChange }: {
  q: Question; value: AnswerValue | undefined; onChange: (v: AnswerValue) => void;
}) {
  // 固定設問の特別描画: ヒアリング相手=人アイコン付き / 実施方法=4択グリッド。
  if (q.questionId === HEARING_QID && q.type === "text") {
    return (
      <div>
        <QLabel q={q} />
        <TextInput value={typeof value === "string" ? value : ""} onChange={onChange} placeholder={q.placeholder} iconLeft={<User size={17} color={T.faint} />} />
      </div>
    );
  }
  if (q.questionId === METHOD_QID && q.type === "radio") {
    return (
      <div>
        <QLabel q={q} />
        <MethodGrid q={q} value={value} onChange={onChange} />
      </div>
    );
  }
  return (
    <div>
      <QLabel q={q} />
      {q.type === "radio" && (() => {
        const cur = radioV(value);
        const curText = value && typeof value === "object" && !Array.isArray(value) ? value.text : "";
        const horizontal = q.options.length === 2 && q.options.every((o) => (o.label || "").length <= 6 && !o.withText);
        return (
          <div>
            <div style={{ display: horizontal ? "flex" : "grid", gridTemplateColumns: horizontal ? undefined : "1fr", gap: 10 }}>
              {q.options.map((o) => {
                const on = cur === o.v;
                const tone = o.tone === "ok" ? T.ok : o.tone === "warn" ? T.warn : T.accent;
                const bg = on ? hexA(tone, 0.08) : "#fff";
                return (
                  <button key={o.v} onClick={() => onChange(o.withText ? { v: o.v, text: cur === o.v ? curText ?? "" : "" } : o.v)}
                    style={{ flex: horizontal ? 1 : undefined, textAlign: "left", background: bg, border: on ? `2px solid ${tone}` : "1px solid #E2E8F0", borderRadius: 12, padding: horizontal ? "13px 12px" : "13px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: horizontal ? "center" : "flex-start", gap: 11 }}>
                    <span style={{ width: 20, height: 20, borderRadius: 9999, border: on ? 0 : "2px solid #CBD5E1", background: on ? tone : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                      {on && <span style={{ width: 8, height: 8, borderRadius: 9999, background: "#fff" }} />}
                    </span>
                    <span style={{ display: "flex", flexDirection: "column", flex: horizontal ? "none" : 1, minWidth: 0 }}>
                      <span style={{ fontSize: 14.5, fontWeight: on ? 700 : 500, color: on ? tone : "#334155" }}>{o.label}</span>
                      {o.hint && <span style={{ fontSize: 11, color: on ? tone : T.faint, marginTop: 1 }}>{o.hint}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
            {q.options.some((o) => o.withText && cur === o.v) && (
              <div style={{ marginTop: 8 }}>
                <TextInput value={curText ?? ""} onChange={(t) => onChange({ v: cur ?? "", text: t })} placeholder="内容を入力" />
              </div>
            )}
          </div>
        );
      })()}

      {q.type === "checkbox" && (() => {
        const arr = Array.isArray(value) ? value : [];
        const toggle = (v: string) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {q.options.map((o) => {
              const on = arr.includes(o.v);
              return (
                <button key={o.v} onClick={() => toggle(o.v)}
                  style={{ width: "100%", textAlign: "left", background: on ? hexA(T.accent, 0.06) : "#fff", border: on ? `1.5px solid ${T.accent}` : "1px solid #E2E8F0", borderRadius: 12, padding: "13px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, border: on ? 0 : "1.5px solid #CBD5E1", background: on ? T.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                    {on && <Check size={14} color="#fff" strokeWidth={3} />}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: on ? 700 : 500, color: on ? T.ink : "#334155" }}>{o.label}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {q.type === "date" && (
        <div>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <Calendar size={18} color={T.faint} />
            </span>
            {/* ネイティブのカレンダーピッカーで入力する (保存形式は従来どおり YYYY/MM/DD)。 */}
            <input type="date" value={toDateInput(typeof value === "string" ? value : "")}
              onChange={(e) => onChange(fromDateInput(e.target.value))}
              style={{ width: "100%", height: 48, padding: "0 14px 0 42px", fontSize: 15, color: T.ink, background: "#fff", border: "2px solid #E2E8F0", borderRadius: 12, outline: "none", boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }} />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {[{ label: "今日", v: todayJst() }, { label: "昨日", v: daysAgoJst(1) }].map((p) => {
              const on = value === p.v;
              return (
                <button key={p.v} onClick={() => onChange(p.v)}
                  style={{ background: on ? hexA(T.accent, 0.08) : "#F1F5F9", border: on ? `1px solid ${T.accent}` : "1px solid transparent", color: on ? T.accent : T.sub, borderRadius: 9999, padding: "5px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {q.type === "text" && (
        <TextInput value={typeof value === "string" ? value : ""} onChange={onChange} placeholder={q.placeholder} multiline={q.multiline} />
      )}

      {q.type === "scale" && (() => {
        const nums: number[] = [];
        for (let v = q.scaleMin; v <= q.scaleMax; v++) nums.push(v);
        return (
          <div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {nums.map((v) => (
                <button key={v} onClick={() => onChange(v)}
                  style={{ height: 40, minWidth: 40, padding: "0 6px", borderRadius: 10, border: value === v ? `2px solid ${T.accent}` : "1px solid #E2E8F0", background: value === v ? T.accent : "#fff", color: value === v ? "#fff" : "#334155", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  {v}
                </button>
              ))}
            </div>
            {(q.minLabel || q.maxLabel) && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: T.faint }}>
                <span>{q.minLabel}</span><span>{q.maxLabel}</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/** スキーマ全体の描画 (分岐対応・タップ回答本体)。 */
export function SchemaForm({ survey, answers, onChange }: {
  survey: Survey; answers: AnswerMap; onChange: (questionId: string, v: AnswerValue) => void;
}) {
  const vis = answerMapToVisMap(answers);
  const rows = survey.questions.filter((q) => isQuestionVisible(q, vis));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {rows.map((q) => (
        <div key={q.questionId} style={q.phase !== "always" ? { animation: "uqUp 220ms ease both" } : undefined}>
          <QuestionField q={q} value={answers[q.questionId]} onChange={(v) => onChange(q.questionId, v)} />
        </div>
      ))}
    </div>
  );
}
